import {
  ChecklistFieldDef,
  ChecklistSectionDef,
  FRONT_OFFICE_CATALOG,
  normalizeDeedIdForSelect,
  repeatableEntriesKey,
  sectionFlowMode,
} from "../components/DynamicChecklistManagement/DynamicChecklistManagement";
import {
  isChecklistFalsyScalar,
  isChecklistTruthyScalar,
  normalizeMappedDropdownValue,
} from "./dynamicChecklistDropdownValue";
import {
  prefillFieldIfEmpty,
  resolveScrutinyFieldTag,
  normalizeMissingSheetsScalar,
  isMissingSheetsField,
} from "./dynamicChecklistScrutinyPrefill";
import {
  resolveSurveyMappedFieldTag,
  surveyHierarchyLinkedGroups,
} from "./dynamicChecklistSurveyHierarchy";
import {
  parseSurveyNatureFieldScopeKey,
  surveyNatureFieldScopeKey,
} from "./dynamicChecklistSurveyNature";

const TAG_KEY_PREFIX = "$tag:";
const LABEL_KEY_PREFIX = "$label:";
const LEGACY_KEY_PREFIX = "$legacy:";
const SCOPED_TAG_KEY_PREFIX = "$scopedtag:";

export function normalizeChecklistLabelKey(label: string | undefined): string {
  return String(label ?? "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

export function isMeaningfulChecklistScalar(value: unknown): boolean {
  const text = value != null ? String(value).trim() : "";
  if (!text) return false;
  if (text === "false") return false;
  return true;
}

/** Every checklist / bank-product field that stores answers in `checklistValues`. */
export function collectAllChecklistFieldsFromMaster(
  master: ChecklistSectionDef[]
): ChecklistFieldDef[] {
  const out: ChecklistFieldDef[] = [];
  master.forEach((section) => {
    (section.fields || []).forEach((f) => out.push(f));
    (section.repeatableGroups || []).forEach((group) => {
      if (group.bankProductId == null && group.naturePropertyKey == null) return;
      (group.fields || []).forEach((f) => out.push(f));
    });
  });
  return out;
}

export function normalizeRestoredScalarValue(
  field: ChecklistFieldDef,
  value: string
): string {
  const raw = String(value ?? "").trim();
  if (!raw) return "";
  if (field.type === "yes_no") {
    if (isChecklistTruthyScalar(raw)) return "Yes";
    if (isChecklistFalsyScalar(raw)) return "No";
    return raw;
  }
  if (field.type === "dropdown") {
    return normalizeMappedDropdownValue(field, raw, resolveScrutinyFieldTag(field));
  }
  if (field.type === "checkbox") {
    return isChecklistTruthyScalar(raw) ? "true" : "false";
  }
  if (isMissingSheetsField(field)) {
    return normalizeMissingSheetsScalar(raw);
  }
  return raw;
}

function lookupSavedScalar(
  saved: Record<string, string>,
  field: ChecklistFieldDef
): string {
  const direct = saved[field.id];
  if (isMeaningfulChecklistScalar(direct)) return String(direct);

  const tag = resolveScrutinyFieldTag(field);
  if (tag) {
    const tagged = saved[`${TAG_KEY_PREFIX}${tag}`] ?? saved[tag];
    if (isMeaningfulChecklistScalar(tagged)) return String(tagged);
  }

  const labelKey = normalizeChecklistLabelKey(field.label);
  if (labelKey) {
    const byLabel = saved[`${LABEL_KEY_PREFIX}${labelKey}`];
    if (isMeaningfulChecklistScalar(byLabel)) return String(byLabel);
  }

  return "";
}

function restoreFollowUpTree(
  nodes: ChecklistFieldDef["optionFollowUps"],
  saved: Record<string, string>,
  out: Record<string, string>
): void {
  if (!nodes?.length) return;
  nodes.forEach((node) => {
    const direct = saved[node.valueKey];
    if (isMeaningfulChecklistScalar(direct)) {
      out[node.valueKey] = normalizeRestoredScalarValue(
        { type: node.questionType, options: node.followUpOptions } as ChecklistFieldDef,
        String(direct)
      );
    }
    const labelKey = normalizeChecklistLabelKey(node.questionLabel);
    if (labelKey) {
      const byLabel = saved[`${LABEL_KEY_PREFIX}${labelKey}`];
      if (isMeaningfulChecklistScalar(byLabel) && !isMeaningfulChecklistScalar(out[node.valueKey])) {
        out[node.valueKey] = normalizeRestoredScalarValue(
          { type: node.questionType, options: node.followUpOptions } as ChecklistFieldDef,
          String(byLabel)
        );
      }
    }
    restoreFollowUpTree(node.nestedFollowUps, saved, out);
  });
}

/**
 * Remap saved answers onto the current master field ids (ids/tags/labels can drift)
 * and normalize yes/no + dropdown values for display controls.
 */
export function restoreChecklistValuesFromPersist(
  master: ChecklistSectionDef[],
  saved: Record<string, string>
): Record<string, string> {
  const out = { ...saved };

  collectAllChecklistFieldsFromMaster(master).forEach((field) => {
    const resolved = lookupSavedScalar(saved, field);
    if (isMeaningfulChecklistScalar(resolved)) {
      out[field.id] = normalizeRestoredScalarValue(field, resolved);
    } else if (out[field.id] != null) {
      const normalized = normalizeRestoredScalarValue(field, String(out[field.id]));
      if (normalized) out[field.id] = normalized;
    }
  });

  master.forEach((section) => {
    (section.fields || []).forEach((field) => {
      restoreFollowUpTree(field.optionFollowUps, saved, out);
    });
    (section.repeatableGroups || []).forEach((group) => {
      if (group.bankProductId == null && group.naturePropertyKey == null) return;
      (group.fields || []).forEach((field) => {
        restoreFollowUpTree(field.optionFollowUps, saved, out);
      });
    });
  });

  return out;
}

/** Duplicate stable tag/label keys when persisting so reload survives master id changes. */
export function enrichChecklistValuesForPersist(
  master: ChecklistSectionDef[],
  checklistValues: Record<string, string>
): Record<string, string> {
  const out = { ...checklistValues };

  const indexField = (field: ChecklistFieldDef, raw: string) => {
    const val = String(raw ?? "").trim();
    if (!isMeaningfulChecklistScalar(val)) return;
    const tag = resolveScrutinyFieldTag(field);
    if (tag) out[`${TAG_KEY_PREFIX}${tag}`] = val;
    const labelKey = normalizeChecklistLabelKey(field.label);
    if (labelKey) out[`${LABEL_KEY_PREFIX}${labelKey}`] = val;
  };

  collectAllChecklistFieldsFromMaster(master).forEach((field) => {
    indexField(field, out[field.id] ?? "");
  });

  // Nature-scoped survey fields (sv:fd:schedule:fieldId) — stable tag restore per schedule.
  const fieldById = new Map<string, ChecklistFieldDef>();
  collectAllChecklistFieldsFromMaster(master).forEach((field) => {
    fieldById.set(field.id, field);
  });
  Object.keys(checklistValues).forEach((key) => {
    const parsed = parseSurveyNatureFieldScopeKey(key);
    if (!parsed) return;
    const val = String(checklistValues[key] ?? "").trim();
    if (!isMeaningfulChecklistScalar(val)) return;
    const field = fieldById.get(parsed.fieldId);
    if (!field) return;
    const tag = resolveSurveyMappedFieldTag(field);
    if (!tag) return;
    out[`${SCOPED_TAG_KEY_PREFIX}${tag}::${parsed.fdId}::${parsed.scheduleId}`] = val;
  });

  master.forEach((section) => {
    const walkFollowUps = (nodes: ChecklistFieldDef["optionFollowUps"]) => {
      if (!nodes?.length) return;
      nodes.forEach((node) => {
        const val = out[node.valueKey];
        if (isMeaningfulChecklistScalar(val)) {
          const labelKey = normalizeChecklistLabelKey(node.questionLabel);
          if (labelKey) out[`${LABEL_KEY_PREFIX}${labelKey}`] = String(val);
        }
        walkFollowUps(node.nestedFollowUps);
      });
    };
    (section.fields || []).forEach((f) => walkFollowUps(f.optionFollowUps));
    (section.repeatableGroups || []).forEach((group) => {
      if (group.bankProductId == null && group.naturePropertyKey == null) return;
      (group.fields || []).forEach((f) => walkFollowUps(f.optionFollowUps));
    });
  });

  return out;
}

/** Index survey hierarchy repeatable row values by mapped tag for stable restore. */
export function enrichRepeatableValuesForPersist(
  master: ChecklistSectionDef[],
  repeatableValues: Record<string, Record<string, string>[]>
): Record<string, Record<string, string>[]> {
  const out: Record<string, Record<string, string>[]> = {};
  Object.keys(repeatableValues).forEach((key) => {
    out[key] = (repeatableValues[key] || []).map((row) => ({ ...row }));
  });

  master.forEach((section) => {
    if (sectionFlowMode(section) !== "survey_verification") return;
    surveyHierarchyLinkedGroups(section).forEach((group) => {
      const key = repeatableEntriesKey(section.id, group.id);
      (out[key] || []).forEach((row) => {
        (group.fields || []).forEach((field) => {
          const val = String(row[field.id] ?? "").trim();
          if (!isMeaningfulChecklistScalar(val)) return;
          const tag = resolveSurveyMappedFieldTag(field);
          if (tag) row[`${TAG_KEY_PREFIX}${tag}`] = val;
        });
      });
    });
  });

  return out;
}

/** Restore nature-scoped survey answers from `$scopedtag:` keys saved in checklistValues. */
export function restoreNatureScopedFieldsFromPersist(
  master: ChecklistSectionDef[],
  checklistValues: Record<string, string>
): void {
  const fieldById = new Map<string, ChecklistFieldDef>();
  collectAllChecklistFieldsFromMaster(master).forEach((field) => {
    fieldById.set(field.id, field);
  });

  Object.keys(checklistValues).forEach((key) => {
    if (!key.startsWith(SCOPED_TAG_KEY_PREFIX)) return;
    const body = key.slice(SCOPED_TAG_KEY_PREFIX.length);
    const parts = body.split("::");
    if (parts.length < 3) return;
    const tag = parts[0];
    const fdId = parts[1];
    const scheduleId = parts[2];
    const val = checklistValues[key];
    if (!isMeaningfulChecklistScalar(val)) return;

    let field: ChecklistFieldDef | undefined;
    for (const f of fieldById.values()) {
      if (resolveSurveyMappedFieldTag(f) === tag) {
        field = f;
        break;
      }
    }
    if (!field) return;
    const scopedKey = surveyNatureFieldScopeKey(fdId, scheduleId, field.id);
    checklistValues[scopedKey] = normalizeRestoredScalarValue(field, String(val));
  });

  // Direct sv:fd:schedule:fieldId keys are already in checklistValues; normalize them.
  fieldById.forEach((field) => {
    Object.keys(checklistValues).forEach((key) => {
      const parsed = parseSurveyNatureFieldScopeKey(key);
      if (!parsed || parsed.fieldId !== field.id) return;
      const val = checklistValues[key];
      if (!isMeaningfulChecklistScalar(val)) return;
      checklistValues[key] = normalizeRestoredScalarValue(field, String(val));
    });
  });
}

/** Restore hierarchy repeatable answers from `$tag:` keys saved on each row. */
export function restoreRepeatableHierarchyFieldsFromPersist(
  master: ChecklistSectionDef[],
  repeatableValues: Record<string, Record<string, string>[]>
): void {
  master.forEach((section) => {
    if (sectionFlowMode(section) !== "survey_verification") return;
    surveyHierarchyLinkedGroups(section).forEach((group) => {
      const key = repeatableEntriesKey(section.id, group.id);
      (repeatableValues[key] || []).forEach((row) => {
        (group.fields || []).forEach((field) => {
          const tag = resolveSurveyMappedFieldTag(field);
          if (!tag) return;
          const tagged = row[`${TAG_KEY_PREFIX}${tag}`];
          if (isMeaningfulChecklistScalar(tagged)) {
            row[field.id] = normalizeRestoredScalarValue(field, String(tagged));
          }
        });
      });
    });
  });
}

/** Pull legacy `generalVerification` / front-office keys into stable restore keys. */
export function seedLegacyChecklistRestoreKeys(
  rawJson: Record<string, unknown> | null | undefined,
  checklistValues: Record<string, string>
): Record<string, string> {
  if (!rawJson || typeof rawJson !== "object") return checklistValues;
  const out = { ...checklistValues };
  const gv = rawJson.generalVerification;
  if (gv && typeof gv === "object") {
    const row = gv as Record<string, unknown>;
    if (row.isSpellingCorrect === true) {
      out[`${LEGACY_KEY_PREFIX}isSpellingCorrect`] = "Yes";
    }
    if (row.hintAbtfile === true) {
      out[`${LEGACY_KEY_PREFIX}hintAbtfile`] = "Yes";
    }
    const ownerShip = row.ownerShip;
    if (ownerShip && typeof ownerShip === "object") {
      const value = (ownerShip as Record<string, unknown>).value;
      if (value != null && String(value).trim()) {
        out[`${LEGACY_KEY_PREFIX}ownerShip`] = String(value).trim();
      }
    }
    const freeHold = row.freeRleasehold;
    if (freeHold && typeof freeHold === "object") {
      const value = (freeHold as Record<string, unknown>).value;
      if (value != null && String(value).trim()) {
        out[`${LEGACY_KEY_PREFIX}freeRleasehold`] = String(value).trim();
      }
    }
  }
  const doctype = rawJson.doctype;
  if (doctype != null && String(doctype).trim()) {
    out[`${LEGACY_KEY_PREFIX}doctype`] = String(doctype).trim();
  }
  return out;
}

function applyLegacySeeds(
  master: ChecklistSectionDef[],
  checklistValues: Record<string, string>
): Record<string, string> {
  const out = { ...checklistValues };
  const legacy = {
    isSpellingCorrect: out[`${LEGACY_KEY_PREFIX}isSpellingCorrect`],
    hintAbtfile: out[`${LEGACY_KEY_PREFIX}hintAbtfile`],
    ownerShip: out[`${LEGACY_KEY_PREFIX}ownerShip`],
    freeRleasehold: out[`${LEGACY_KEY_PREFIX}freeRleasehold`],
    doctype: out[`${LEGACY_KEY_PREFIX}doctype`],
  };

  collectAllChecklistFieldsFromMaster(master).forEach((field) => {
    if (isMeaningfulChecklistScalar(out[field.id])) return;
    const normLabel = normalizeChecklistLabelKey(field.label);
    const tag = resolveScrutinyFieldTag(field);

    if (
      field.type === "yes_no" &&
      normLabel.includes("spelling") &&
      isMeaningfulChecklistScalar(legacy.isSpellingCorrect)
    ) {
      out[field.id] = normalizeRestoredScalarValue(field, legacy.isSpellingCorrect);
      return;
    }
    if (
      field.type === "yes_no" &&
      normLabel.includes("hint") &&
      normLabel.includes("file") &&
      isMeaningfulChecklistScalar(legacy.hintAbtfile)
    ) {
      out[field.id] = normalizeRestoredScalarValue(field, legacy.hintAbtfile);
      return;
    }
    if (
      field.type === "dropdown" &&
      normLabel.includes("constitution") &&
      isMeaningfulChecklistScalar(legacy.ownerShip)
    ) {
      out[field.id] = normalizeRestoredScalarValue(field, legacy.ownerShip);
      return;
    }
    if (
      field.type === "dropdown" &&
      (normLabel.includes("free hold") || normLabel.includes("lease hold")) &&
      isMeaningfulChecklistScalar(legacy.freeRleasehold)
    ) {
      out[field.id] = normalizeRestoredScalarValue(field, legacy.freeRleasehold);
      return;
    }
    if (
      field.type === "dropdown" &&
      (normLabel.includes("type of loan") || tag === "doctype") &&
      isMeaningfulChecklistScalar(legacy.doctype)
    ) {
      out[field.id] = normalizeRestoredScalarValue(field, legacy.doctype);
    }
  });

  return out;
}

function prefillFrontOfficeFieldIfEmpty(
  field: ChecklistFieldDef,
  target: Record<string, string>,
  frontOfficeRow: Record<string, string>
): void {
  if (field.dynamicValueSource !== "front_office") return;
  const id = normalizeDeedIdForSelect(field.scrutinyLinkedDeedId);
  if (!id || !FRONT_OFFICE_CATALOG.some((c) => c.id === id)) return;
  if (isMeaningfulChecklistScalar(target[field.id])) return;
  const src = frontOfficeRow[id];
  if (src == null || !String(src).trim()) return;
  if (field.type === "dropdown") {
    target[field.id] = normalizeMappedDropdownValue(field, String(src), id);
  } else if (field.type === "text" || field.type === "date" || field.type === "remarks") {
    target[field.id] = String(src);
  }
}

/**
 * On restore: fill empty general-verification scalars from scrutiny / front office
 * without rebuilding survey / EC / deed repeatable hierarchies.
 */
export function applyScrutinyScalarPrefillOnly(
  master: ChecklistSectionDef[],
  checklistValues: Record<string, string>,
  scrutinyApiData: any,
  frontOfficeRow: Record<string, string>
): void {
  const propertyDetails =
    scrutinyApiData?.propertyDetails && typeof scrutinyApiData.propertyDetails === "object"
      ? Object.keys(scrutinyApiData.propertyDetails).reduce<Record<string, string>>(
          (acc, key) => {
            const value = (scrutinyApiData.propertyDetails as Record<string, unknown>)[key];
            acc[key] = value == null ? "" : String(value);
            return acc;
          },
          {}
        )
      : null;

  const prefillField = (field: ChecklistFieldDef) => {
    prefillFrontOfficeFieldIfEmpty(field, checklistValues, frontOfficeRow);
    if (propertyDetails) {
      prefillFieldIfEmpty(checklistValues, field, propertyDetails);
    }
  };

  master.forEach((section) => {
    (section.fields || []).forEach(prefillField);
    (section.repeatableGroups || []).forEach((group) => {
      if (group.bankProductId == null && group.naturePropertyKey == null) return;
      (group.fields || []).forEach(prefillField);
    });
  });
}

/** Full restore pipeline for saved `checklistValues` blobs. */
export function rebuildChecklistValuesFromPersist(
  master: ChecklistSectionDef[],
  saved: Record<string, string>,
  scrutinyApiData: any,
  frontOfficeRow: Record<string, string>
): Record<string, string> {
  let next = restoreChecklistValuesFromPersist(master, saved);
  next = applyLegacySeeds(master, next);
  applyScrutinyScalarPrefillOnly(master, next, scrutinyApiData, frontOfficeRow);
  return next;
}
